/**
 * Per-device cache of breach ("exposed") verdicts, so the checkup does not hit
 * HIBP (via our proxy) for every password every time it opens. A verdict is
 * reused for up to a week, and re-checked sooner only when the item changed
 * (a password edit bumps `updatedAt`). localStorage, guarded; stores clean
 * verdicts too, so unexposed passwords aren't re-checked weekly either.
 *
 * Per-device in v1 (not synced). The spec's encrypted-in-item cache (§7) —
 * which would survive sync across devices — remains a possible future upgrade.
 */

export interface Verdict {
  exposed: boolean;
  /** Breach prevalence when exposed; 0 when clean. */
  count: number;
  /** epoch ms when this verdict was computed. */
  checkedAt: number;
}

const KEY_PREFIX = 'hbs:checkup:verdict:';

/** A verdict is re-checked at most once per this window — "only go once a week
 *  at most" per password. */
export const BREACH_RECHECK_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function getVerdict(itemId: string): Verdict | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + itemId);
    if (!raw) return null;
    const v = JSON.parse(raw) as Verdict;
    return typeof v?.checkedAt === 'number' && typeof v?.exposed === 'boolean' ? v : null;
  } catch {
    return null;
  }
}

export function setVerdict(itemId: string, v: Verdict): void {
  try {
    localStorage.setItem(KEY_PREFIX + itemId, JSON.stringify(v));
  } catch {
    /* ignore — cache is a per-device convenience */
  }
}

/**
 * True when a cached verdict can be reused: within the re-check window AND the
 * item has not been edited since it was checked (a password change bumps
 * `updatedAt`, which forces a fresh check). `now` is injectable for tests.
 */
export function isFresh(v: Verdict, itemUpdatedAt?: string, now: number = Date.now()): boolean {
  if (now - v.checkedAt > BREACH_RECHECK_MAX_AGE_MS) return false;
  if (itemUpdatedAt) {
    const updated = Date.parse(itemUpdatedAt);
    if (Number.isFinite(updated) && updated > v.checkedAt) return false;
  }
  return true;
}
